import React from 'react';
import { observer } from 'mobx-react';

import { analytics } from 'core/analytics';
import { routerStore } from 'core/routing';

import { frameStore } from 'apps/components/Frame/appManager';
import { clientStore } from 'apps/client/app';
import { useConversionsStore } from 'apps/hooks';
import { CAD_TASK_STATUS } from 'apps/conversions/appManager';
import { getFormatObject } from 'libs/rendering/utils';

import { Spinner, Icon } from 'components';

import {
  StyledAccordion,
  StyledAccordionItem,
  StyledAccordionPanel,
  DownloadTextMain,
  DownloadTextSecondary,
  StyledAccordionButton,
  DownloadIcon,
  DownloadEntryRow,
} from './downloadButton.styled';

const DownloadButton = observer(() => {
  const conversionsStore = useConversionsStore();

  const { route } = routerStore;
  const { file } = frameStore;
  if (!file) return null;
  if (!file.canDownload) return null;
  if (!clientStore.signedInProfile) return null;
  if (route.name === 'share-file') return null;

  const { formatObject, extension: originalExt } = getFormatObject(file);
  const pathParts = file.filename.split('.');
  if (pathParts.length < 2) {
    return null;
  }
  const formatsWithoutConversions = [
    'pdf',
    'jpg',
    'png',
    'txt',
    '.md',
    '.svg',
    '.sql',
  ];

  // - if this format is for the GltfViewer, but its gltf conversion
  // failed, then converting it to stl will likely fail too, so we
  // push the format into formatsWithoutConversions so that we
  // only add the download for the original file into the dropdown
  // but no other conversions
  if (
    formatObject.viewer === 'GltfViewer' &&
    file.conversionGltfjson?.status !== 'SUCCESS'
  ) {
    formatsWithoutConversions.push(originalExt);
  }

  // - if this is a format that is not for the GltfViewer at all only
  // allow download of original file but no conversions (same as above)
  if (formatObject.viewer !== 'GltfViewer') {
    formatsWithoutConversions.push(originalExt);
  }

  const downloadFormats = [];
  downloadFormats.push(originalExt);
  if (!formatsWithoutConversions.includes(originalExt)) {
    if (originalExt !== 'stl') {
      downloadFormats.push('stl');
    }
    // check if this is a file format for which we want
    // to disable step conversions
    const disableStepFormats = ['stl', 'dxf', 'dwg', 'step', 'stp'];
    if (!disableStepFormats.includes(originalExt)) {
      downloadFormats.push('step');
    }
  }

  const downloadButtons = downloadFormats.map(downloadExt => {
    let statusIcon = <Icon name='download' />;

    const conversionOperationKey = `${file.id}-${downloadExt}`;
    const operation = conversionsStore.conversionOperations[conversionOperationKey];

    const conversionStatus = operation?.status;
    if (conversionStatus === CAD_TASK_STATUS.CONVERTING)
      statusIcon = <Spinner size='small' isDark />;
    else if (conversionStatus === CAD_TASK_STATUS.FAILED)
      statusIcon = <Icon name='error' />;
    // else if (conversionStatus === CAD_TASK_STATUS.FINISHED)
    //   statusIcon = <Icon name='download' />;

    let text = `Download as .${downloadExt.toLowerCase()}`;
    let secondaryText = '';
    if (downloadExt === originalExt) {
      secondaryText = '(Original file)';
    } else if (operation && operation.statusText !== '') {
      text = operation.statusText;
    }

    const downloadEntry = (
      <DownloadEntryRow>
        <DownloadTextMain>{text}</DownloadTextMain>
        <DownloadTextSecondary>{secondaryText}</DownloadTextSecondary>
        {statusIcon}
      </DownloadEntryRow>
    );

    // - need to make sure we don't request a conversion for the (original) originalExt,
    // so this returns either a dumb download button that just uses the permalink,
    // or a button that triggers a new conversion
    if (downloadExt === originalExt) {
      return (
        <DownloadIcon
          onClick={() => {
            analytics.sendEvent('viewer-downloaded-project');
          }}
          href={file?.permalink}
          download
          target='_blank'
          rel='noreferrer'
          key={`download-orignal-${originalExt}`}
        >
          {downloadEntry}
        </DownloadIcon>
      );
    }
    return (
      <DownloadIcon
        onClick={() => {
          analytics.sendEvent('viewer-downloaded-project');
          conversionsStore.StartNewConversionOperation(file.id, downloadExt);
        }}
        download
        target='_blank'
        rel='noreferrer'
        key={`download-conversion-${downloadExt}`}
      >
        {downloadEntry}
      </DownloadIcon>
    );
  });

  return (
    <StyledAccordion allowToggle>
      <StyledAccordionItem>
        <StyledAccordionButton fontSize='0.875rem'>
          Download
          {/* <p color='white'>Download</p> */}
          <Icon
            style={{ marginLeft: '0.5em', marginRight: '0.5em' }}
            name='download'
            size='default'
          />
          <Icon name='caret_down' size='0.75em' />
        </StyledAccordionButton>

        <StyledAccordionPanel>{downloadButtons}</StyledAccordionPanel>
      </StyledAccordionItem>
    </StyledAccordion>
  );
});

export default DownloadButton;
